/**
 * Secondary identifiers for bare org-name renders (self-serve-org-signup task 7.9):
 * `Org.name` lost its `unique=True` constraint, so two organisations can now share a
 * name and any list rendering just `org.name` becomes ambiguous. `short_name` is the
 * natural secondary identifier — always populated on the model (`blank=False`), even
 * though it isn't itself guaranteed unique — but some list endpoints (portfolio,
 * site-admin billing) only project id/name, so a short id fragment is the fallback.
 */

/** `"name (secondary)"`, or bare `name` when there is nothing to disambiguate with. */
export function withSecondaryIdentifier(name, secondary) {
    if (!secondary) return name ?? ''
    return `${name ?? ''} (${secondary})`
}

/** A short, stable fragment of an id — not pretty, but always available and distinguishing. */
export function shortIdFragment(id) {
    return id ? `#${String(id).slice(0, 8)}` : ''
}

/**
 * The secondary identifier to actually display: prefer the org's real short name, falling
 * back to a short id fragment when it's absent or blank. `short_name` is `blank=False` on
 * the `Org` model, but that isn't enforced by every projection that can reach the frontend
 * (and it isn't unique either) — a blank secondary rendering as an empty `()` is worse than
 * a hex one, so this never returns an empty string while `id` is available.
 */
export function preferredSecondary(shortName, id) {
    const trimmed = typeof shortName === 'string' ? shortName.trim() : ''
    return trimmed || shortIdFragment(id)
}
